import { type SelectionsMachine } from '../../machine/selection-machine.ts';
import { type SelectionInput } from '../types.ts';

interface SelectionProjectionAdapter {
  getVisibleEntryIds(): string[];
  getVisibleFieldIds(): string[];
}

interface SelectionProjection {
  entryIds: string[];
  fieldIds: string[];
}

interface ProjectionChangesInputOptions {
  subscribe: (onChange: VoidFunction) => VoidFunction;
  isProjectionAvailable?: () => boolean;
}

function haveSameOrderedIds(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

/**
 * Reconciles index-based selections against the canonical database-view projection.
 *
 * The adapter remains the sole authority for visible IDs and display order. The
 * injected subscription only invalidates that projection. While the projection is
 * unavailable, this input performs no adapter reads and retains its last valid IDs.
 * The first valid projection establishes a baseline; later valid projections use the
 * existing stable-ID reconciliation transition.
 */
export function createProjectionChangesInput(
  machine: SelectionsMachine,
  adapter: SelectionProjectionAdapter,
  { subscribe, isProjectionAvailable = () => true }: ProjectionChangesInputOptions
): SelectionInput {
  let unsubscribeProjection: VoidFunction | undefined;
  let unsubscribeMachine: VoidFunction | undefined;
  let current: SelectionProjection | undefined;

  const readProjection = (): SelectionProjection | undefined => {
    if (!isProjectionAvailable()) {
      return undefined;
    }

    return {
      entryIds: adapter.getVisibleEntryIds(),
      fieldIds: adapter.getVisibleFieldIds()
    };
  };

  const reconcileIfChanged = () => {
    if (machine.getState().mode === 'idle') {
      current = undefined;

      return;
    }

    const next = readProjection();

    if (next === undefined) {
      return;
    }

    if (current === undefined) {
      current = next;

      return;
    }

    if (
      haveSameOrderedIds(current.entryIds, next.entryIds) &&
      haveSameOrderedIds(current.fieldIds, next.fieldIds)
    ) {
      return;
    }

    const previous = current;

    current = next;
    machine.dispatch({
      type: 'reconcileSelections',
      source: 'api',
      entries: { prev: previous.entryIds, next: next.entryIds },
      fields: { prev: previous.fieldIds, next: next.fieldIds }
    });
  };

  return {
    setup() {
      if (machine.getState().mode !== 'idle') {
        current = readProjection();
      }

      unsubscribeMachine = machine.subscribe((_delta, state) => {
        if (state.mode === 'idle') {
          current = undefined;

          return;
        }

        // Idle has no projection-relative indices, so defer adapter work until
        // the first selection establishes the baseline it can later reconcile.
        current ??= readProjection();
      });
      unsubscribeProjection = subscribe(reconcileIfChanged);
    },

    dispose() {
      unsubscribeProjection?.();
      unsubscribeProjection = undefined;
      unsubscribeMachine?.();
      unsubscribeMachine = undefined;
      current = undefined;
    }
  };
}
